import { useState, useCallback } from "react";
import { jsPDF } from "jspdf";
import logo from "./assets/logo-clovai.svg";
import "./App.css";

const MODULOS = [
  "Agendamiento",
  "Asistencial",
  "Cuentas Médicas",
  "Abastecimiento",
  "Almacén",
  "Talento Humano",
  "Nómina",
  "Contabilidad",
  "SST",
  "Ajustes",
  "Agendamiento (Dinámica)",
  "Portal Pacientes",
  "Portal Trabajadores",
  "Otro",
];

const TIPOS = ["Mejora", "Ley", "Otro"];

const INITIAL = {
  empresa: "",
  ticket: "",
  modulo: "",
  tipos: [],
  fecha: new Date().toISOString().split("T")[0],
  responsable: "",
  q1: "",
  q2: "",
  q3: "",
};

function validate(f) {
  const errs = {};
  if (!f.empresa.trim()) errs.empresa = "Requerido";
  if (!f.ticket.trim()) errs.ticket = "Requerido";
  if (!f.modulo) errs.modulo = "Seleccione un módulo";
  if (!f.tipos.length) errs.tipos = "Seleccione al menos uno";
  if (!f.fecha) errs.fecha = "Requerido";
  if (!f.responsable.trim()) errs.responsable = "Requerido";
  if (!f.q1.trim()) errs.q1 = "Requerido";
  if (!f.q2.trim()) errs.q2 = "Requerido";
  if (!f.q3.trim()) errs.q3 = "Requerido";
  return errs;
}

async function getSummary(data) {
  try {
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 1000,
        messages: [{
          role: "user",
          content: `Eres el sistema de gestión de requerimientos de Clovai (HIS salud ambulatoria LATAM).
Genera un resumen ejecutivo en español, máximo 120 palabras, un solo párrafo fluido, tono profesional y directo.

Empresa: ${data.empresa} | Ticket: ${data.ticket} | Módulo: ${data.modulo}
Tipo: ${data.tipos.join(", ")} | Responsable: ${data.responsable}
Descripción: ${data.q1}
Necesidad: ${data.q2}
Comportamiento esperado: ${data.q3}`
        }]
      })
    });
    const json = await res.json();
    return json.content?.[0]?.text || fallbackSummary(data);
  } catch {
    return fallbackSummary(data);
  }
}

function fallbackSummary(d) {
  return `Requerimiento de tipo ${d.tipos.join("/")} para el módulo ${d.modulo} de ${d.empresa}, presentado por ${d.responsable}. ${d.q1}`;
}

function toImgData(src) {
  return new Promise((res) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      c.getContext("2d").drawImage(img, 0, 0);
      res({ data: c.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = () => res(null);
    img.src = src;
  });
}

async function buildPDF(data, summary) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, M = 20, CW = W - M * 2;
  let y = 0;

  // Header background
  doc.setFillColor(35, 66, 179);
  doc.rect(0, 0, W, 52, "F");
  // Teal accent circle
  doc.setFillColor(99, 230, 221);
  doc.circle(W - 10, 0, 35, "F");
  doc.setFillColor(35, 66, 179);
  doc.circle(W - 10, 0, 32, "F");

  // Logo
  const logoImg = await toImgData(logo);
  if (logoImg) {
    const lh = 14;
    const lw = (logoImg.w / logoImg.h) * lh;
    doc.addImage(logoImg.data, "PNG", M, 10, lw, lh);
    y = 10 + lh + 6;
  } else {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.text("clovai", M, 22);
    y = 30;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(99, 230, 221);
  doc.text("Solicitud de Nueva Implementación", M, y);
  y += 6;
  doc.setFontSize(8);
  doc.setTextColor(180, 200, 255);
  doc.text(`Ticket: ${data.ticket}  ·  Fecha: ${data.fecha}  ·  Empresa: ${data.empresa}`, M, y);

  y = 62;

  // Summary box
  doc.setFillColor(240, 244, 255);
  doc.roundedRect(M, y, CW, 30, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(35, 66, 179);
  doc.text("RESUMEN EJECUTIVO", M + 6, y + 8);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(40, 40, 70);
  const sumLines = doc.splitTextToSize(summary, CW - 12);
  sumLines.slice(0, 4).forEach((l, i) => doc.text(l, M + 6, y + 15 + i * 4.5));
  y += 38;

  // Meta grid
  const meta = [
    ["Empresa", data.empresa],
    ["Módulo / Área", data.modulo],
    ["Tipo de requerimiento", data.tipos.join(", ")],
    ["Responsable", data.responsable],
  ];
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(35, 66, 179);
  doc.text("DATOS GENERALES", M, y);
  y += 4;
  doc.setDrawColor(209, 220, 252);
  doc.setLineWidth(0.3);
  doc.line(M, y, M + CW, y);
  y += 5;
  meta.forEach(([k, v]) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(120, 130, 160);
    doc.text(k, M, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(30, 30, 60);
    doc.text(String(v), M + 50, y);
    y += 6;
  });

  y += 4;

  // Questions
  const qs = [
    ["1. DESCRIPCIÓN DEL REQUERIMIENTO", data.q1],
    ["2. PROBLEMA O NECESIDAD", data.q2],
    ["3. COMPORTAMIENTO ESPERADO", data.q3],
  ];
  qs.forEach(([title, body]) => {
    if (y > 258) { doc.addPage(); y = 20; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(35, 66, 179);
    doc.text(title, M, y);
    y += 4;
    doc.setDrawColor(209, 220, 252);
    doc.line(M, y, M + CW, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(30, 30, 60);
    doc.splitTextToSize(body, CW).forEach((l) => {
      if (y > 272) { doc.addPage(); y = 20; }
      doc.text(l, M, y);
      y += 5;
    });
    y += 6;
  });

  // Footer
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(180, 185, 210);
    doc.text(`Clovai · Unified Systems. Absolute Control. · Pág. ${i} de ${pages}`, W / 2, 290, { align: "center" });
  }

  const fname = `Clovai_${data.ticket.replace(/[^a-z0-9]/gi, "-")}_${data.empresa.replace(/\s+/g, "-")}.pdf`;
  doc.save(fname);
}

export default function App() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [touched, setTouched] = useState({});

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (touched[k]) setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const toggleTipo = (t) => {
    const next = form.tipos.includes(t)
      ? form.tipos.filter((x) => x !== t)
      : [...form.tipos, t];
    set("tipos", next);
  };

  const blur = (k) => {
    setTouched((p) => ({ ...p, [k]: true }));
    const errs = validate(form);
    if (errs[k]) setErrors((p) => ({ ...p, [k]: errs[k] }));
  };

  const filled = Object.keys(INITIAL).filter((k) => {
    if (k === "tipos") return form.tipos.length > 0;
    return String(form[k]).trim() !== "";
  }).length;
  const progress = Math.round((filled / Object.keys(INITIAL).length) * 100);

  const handleSubmit = useCallback(async () => {
    const errs = validate(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      setTouched(Object.fromEntries(Object.keys(INITIAL).map((k) => [k, true])));
      return;
    }
    setStatus("loading");
    try {
      const summary = await getSummary(form);
      await buildPDF(form, summary);
      setStatus("done");
    } catch {
      setStatus("error");
    }
  }, [form]);

  const isLoading = status === "loading";

  return (
    <div className="wrap">
      <div className="form-card">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: progress + "%" }} />
        </div>

        <div className="form-header">
          <div className="header-inner">
            <img src={logo} alt="Clovai" className="header-logo" />
            <div>
              <span className="header-tag">Soporte · Nueva Implementación</span>
              <h1>Solicitud de Requerimiento</h1>
              <p>Complete el formulario. Al enviar, recibirá un PDF con el resumen de su solicitud.</p>
            </div>
          </div>
        </div>

        <div className="form-body">
          <div className="section-label">Datos generales</div>

          <div className="fields-grid">
            <Field label="Empresa" required error={touched.empresa && errors.empresa}>
              <input value={form.empresa} onChange={(e) => set("empresa", e.target.value)} onBlur={() => blur("empresa")} placeholder="Nombre de la institución" />
            </Field>
            <Field label="Ticket" required error={touched.ticket && errors.ticket}>
              <input value={form.ticket} onChange={(e) => set("ticket", e.target.value)} onBlur={() => blur("ticket")} placeholder="Ej. TKT-2024-001" />
            </Field>
            <Field label="Módulo / Área" required error={touched.modulo && errors.modulo}>
              <select value={form.modulo} onChange={(e) => set("modulo", e.target.value)} onBlur={() => blur("modulo")}>
                <option value="">Seleccionar módulo…</option>
                {MODULOS.map((m) => <option key={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Fecha de solicitud" required error={touched.fecha && errors.fecha}>
              <input type="date" value={form.fecha} onChange={(e) => set("fecha", e.target.value)} onBlur={() => blur("fecha")} />
            </Field>
            <Field label="Responsable" required error={touched.responsable && errors.responsable} full>
              <input value={form.responsable} onChange={(e) => set("responsable", e.target.value)} onBlur={() => blur("responsable")} placeholder="Nombre del solicitante" />
            </Field>
          </div>

          <Field label="Tipo de requerimiento" required error={touched.tipos && errors.tipos}>
            <div className="check-group">
              {TIPOS.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`check-item${form.tipos.includes(t) ? " checked" : ""}`}
                  onClick={() => toggleTipo(t)}
                >
                  <span className="check-box">{form.tipos.includes(t) && <CheckIcon />}</span>
                  <span className="check-label">{t}</span>
                </button>
              ))}
            </div>
          </Field>

          <div className="section-label" style={{ marginTop: "1.5rem" }}>Detalle del requerimiento</div>

          <div className="questions-stack">
            <QCard num={1} title="Descripción del requerimiento" sub="¿Qué necesita que el sistema haga o cambie? Describa con el mayor detalle posible." error={touched.q1 && errors.q1}>
              <textarea value={form.q1} onChange={(e) => set("q1", e.target.value)} onBlur={() => blur("q1")} placeholder="Describa qué necesita que el sistema haga o cambie…" />
            </QCard>
            <QCard num={2} title="Problema o necesidad" sub="¿Qué situación, inconveniente u operativa origina esta solicitud?" error={touched.q2 && errors.q2}>
              <textarea value={form.q2} onChange={(e) => set("q2", e.target.value)} onBlur={() => blur("q2")} placeholder="Describa la situación o inconveniente actual…" />
            </QCard>
            <QCard num={3} title="Comportamiento esperado" sub="¿Cómo espera que funcione? Describa el flujo paso a paso si es posible." error={touched.q3 && errors.q3}>
              <textarea value={form.q3} onChange={(e) => set("q3", e.target.value)} onBlur={() => blur("q3")} placeholder="1. El usuario accede a… 2. Selecciona… 3. El sistema…" />
            </QCard>
          </div>
        </div>

        {status === "done" && (
          <div className="status-bar success">
            <span className="status-icon">✓</span>
            <span>PDF generado y descargado correctamente.</span>
          </div>
        )}
        {status === "error" && (
          <div className="status-bar error-bar">
            <span className="status-icon">!</span>
            <span>Ocurrió un error. Intente nuevamente.</span>
          </div>
        )}

        <div className="form-actions">
          <span className="info-note">
            <span className="info-dot" />
            Se generará un PDF resumen para descargar
          </span>
          <button className="btn-submit" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <><span className="spinner" /> Generando…</>
            ) : (
              <><span>↗</span> Enviar solicitud</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, error, full, children }) {
  return (
    <div className={`field-group${full ? " field-full" : ""}${error ? " field-error" : ""}`}>
      <label>{label}{required && <span className="req"> *</span>}</label>
      {children}
      {error && <span className="error-msg">{error}</span>}
    </div>
  );
}

function QCard({ num, title, sub, error, children }) {
  return (
    <div className={`q-card${error ? " field-error" : ""}`}>
      <div className="q-num">{num}</div>
      <div className="q-title">{title}</div>
      <div className="q-sub">{sub}</div>
      {children}
      {error && <span className="error-msg">{error}</span>}
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
      <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
